import { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Chip } from '$lib/components/ui/Chip';
import { Input } from '$lib/components/ui/Input';
import { Popover } from '$lib/components/ui/Popover';
import { Button } from '$lib/components/ui/Button';
import type { ListingStatus, UnitsQuery } from '$lib/api/types';
import { useUnitsQueryState } from '$lib/hooks/useUnitsQueryState';

const SEARCH_DEBOUNCE_MS = 250;

// 6 inline filter chips + "More filters" overflow popover (decision 6).
// Inline: postnummer, m², rooms, year, price, status. Overflow: source, balcony, elevator, energy.

interface RangeChipProps {
  label: string;
  minKey: keyof UnitsQuery;
  maxKey: keyof UnitsQuery;
  unit?: string;
}

function RangeChip({ label, minKey, maxKey, unit }: RangeChipProps) {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  const min = query[minKey] as number | undefined;
  const max = query[maxKey] as number | undefined;
  const active = min != null || max != null;
  const display = active
    ? `${min ?? '–'}${unit ? ` ${unit}` : ''} – ${max ?? '–'}${unit ? ` ${unit}` : ''}`
    : label;
  const count = (min != null ? 1 : 0) + (max != null ? 1 : 0);
  return (
    <Popover
      align="start"
      trigger={
        <span>
          <Chip
            active={active}
            count={count || undefined}
            onClear={active ? () => set({ [minKey]: undefined, [maxKey]: undefined }) : undefined}
          >
            {display}
          </Chip>
        </span>
      }
    >
      {(close) => (
        <div className="flex flex-col gap-2">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              placeholder={t('common.min')}
              defaultValue={min ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : Number(e.target.value);
                set({ [minKey]: v });
              }}
            />
            <span className="text-fg-subtle">–</span>
            <Input
              type="number"
              inputMode="numeric"
              placeholder={t('common.max')}
              defaultValue={max ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : Number(e.target.value);
                set({ [maxKey]: v });
              }}
            />
          </div>
          <div className="flex justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => set({ [minKey]: undefined, [maxKey]: undefined })}
              disabled={!active}
            >
              {t('common.reset')}
            </Button>
            <Button variant="primary" size="sm" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      )}
    </Popover>
  );
}

const STATUS_VALUES: ListingStatus[] = ['active', 'pending', 'rented', 'delisted'];

function PostnummerChip() {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  const active = (query.postnummer?.length ?? 0) > 0;
  const display = active ? `${query.postnummer![0]}${query.postnummer!.length > 1 ? ` +${query.postnummer!.length - 1}` : ''}` : t('filters.postnummer');
  return (
    <Popover
      align="start"
      trigger={
        <span>
          <Chip
            active={active}
            count={active ? query.postnummer!.length : undefined}
            onClear={active ? () => set({ postnummer: [] }) : undefined}
          >
            {display}
          </Chip>
        </span>
      }
    >
      {(close) => (
        <div className="flex flex-col gap-2">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('filters.postnummer')}</div>
          <Input
            type="text"
            placeholder={t('filters.postnummerPlaceholder')}
            defaultValue={(query.postnummer ?? []).join(', ')}
            onBlur={(e) => {
              const v = e.target.value
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean);
              set({ postnummer: v });
            }}
          />
          <div className="flex justify-between pt-1">
            <Button variant="ghost" size="sm" onClick={() => set({ postnummer: [] })} disabled={!active}>
              {t('common.reset')}
            </Button>
            <Button variant="primary" size="sm" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      )}
    </Popover>
  );
}

const PROPERTY_TYPE_VALUES = ['apartment', 'house', 'room'] as const;
type PropertyTypeValue = (typeof PROPERTY_TYPE_VALUES)[number];

function PropertyTypeChip() {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  const selected = new Set((query.propertyType ?? []) as PropertyTypeValue[]);
  const active = selected.size > 0;
  const display = active
    ? t('filters.propertyTypeCount', { count: selected.size })
    : t('filters.propertyType');
  function toggle(value: PropertyTypeValue) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    set({ propertyType: Array.from(next) });
  }
  return (
    <Popover
      align="start"
      trigger={
        <span>
          <Chip
            active={active}
            count={active ? selected.size : undefined}
            onClear={active ? () => set({ propertyType: [] }) : undefined}
          >
            {display}
          </Chip>
        </span>
      }
    >
      {(close) => (
        <div className="flex flex-col gap-1.5">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('filters.propertyType')}</div>
          {PROPERTY_TYPE_VALUES.map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.has(value)}
                onChange={() => toggle(value)}
                className="h-3.5 w-3.5 accent-accent"
              />
              <span>{t(`filters.propertyTypeOption.${value}`)}</span>
            </label>
          ))}
          <div className="flex justify-between pt-1">
            <Button variant="ghost" size="sm" onClick={() => set({ propertyType: [] })} disabled={!active}>
              {t('common.reset')}
            </Button>
            <Button variant="primary" size="sm" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      )}
    </Popover>
  );
}

function StatusChip() {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  const selected = new Set(query.status ?? []);
  const active = selected.size > 0;
  const display = active ? t('filters.statusCount', { count: selected.size }) : t('filters.status');
  function toggle(value: ListingStatus) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    set({ status: Array.from(next) });
  }
  return (
    <Popover
      align="start"
      trigger={
        <span>
          <Chip
            active={active}
            count={active ? selected.size : undefined}
            onClear={active ? () => set({ status: [] }) : undefined}
          >
            {display}
          </Chip>
        </span>
      }
    >
      {(close) => (
        <div className="flex flex-col gap-1.5">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('filters.status')}</div>
          {STATUS_VALUES.map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.has(value)}
                onChange={() => toggle(value)}
                className="h-3.5 w-3.5 accent-accent"
              />
              <span>{t(`status.${value}`)}</span>
            </label>
          ))}
          <div className="flex justify-between pt-1">
            <Button variant="ghost" size="sm" onClick={() => set({ status: [] })} disabled={!active}>
              {t('common.reset')}
            </Button>
            <Button variant="primary" size="sm" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      )}
    </Popover>
  );
}

function MoreFiltersChip() {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  // Overflow chip for source + future balcony/elevator/energy (decision 6).
  // Source is the only one wired to data right now; the rest are placeholders
  // surfaced behind the "More filters" facet for parity with the brief.
  const sourceCount = query.source?.length ?? 0;
  const overflowCount = sourceCount;
  return (
    <Popover
      align="end"
      trigger={
        <span>
          <Chip icon={<Filter size={12} />} active={overflowCount > 0} count={overflowCount || undefined}>
            {t('filters.moreFilters')}
          </Chip>
        </span>
      }
    >
      {() => (
        <div className="flex w-72 flex-col gap-3">
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('filters.source')}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(['BoligPortal', 'Lejebolig', 'Lejeboligportal', 'BoligZonen'] as const).map((s) => {
                const selected = (query.source ?? []).includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      const cur = new Set(query.source ?? []);
                      if (cur.has(s)) cur.delete(s);
                      else cur.add(s);
                      set({ source: Array.from(cur) });
                    }}
                    className={
                      'rounded-xs border px-2 py-0.5 text-2xs ' +
                      (selected
                        ? 'border-accent/50 bg-accent/10 text-accent'
                        : 'border-rule-strong bg-surface text-fg-muted hover:bg-surface-2')
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('filters.pendingBbr')}</div>
            <ul className="mt-1 space-y-1 text-xs text-fg-subtle">
              <li>{t('filters.balconyElevatorPending')}</li>
              <li>{t('filters.energyPending')}</li>
            </ul>
          </div>
        </div>
      )}
    </Popover>
  );
}

function SearchInput() {
  const { query, set } = useUnitsQueryState();
  const { t } = useTranslation();
  const urlValue = query.q ?? '';
  const [value, setValue] = useState(urlValue);
  const lastPushed = useRef(urlValue);

  // Pull external URL changes (e.g. clear-filters) back into the input.
  useEffect(() => {
    if (urlValue !== lastPushed.current) {
      setValue(urlValue);
      lastPushed.current = urlValue;
    }
  }, [urlValue]);

  // Debounce keystrokes → URL writes so we issue one fetch per pause,
  // not one per character.
  useEffect(() => {
    if (value === lastPushed.current) return;
    const handle = setTimeout(() => {
      lastPushed.current = value;
      set({ q: value === '' ? undefined : value });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value, set]);

  return (
    <Input
      type="search"
      placeholder={t('filters.filterByAddress')}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      className="h-7 w-56"
      aria-label={t('filters.filterByAddress')}
    />
  );
}

export function FilterChipStrip() {
  const { hasFilters, clear, filterCount } = useUnitsQueryState();
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-rule bg-surface px-3 py-2" data-testid="filter-strip">
      <SearchInput />
      <PostnummerChip />
      <RangeChip label={t('filters.m2')} minKey="m2Min" maxKey="m2Max" unit="m²" />
      <RangeChip label={t('filters.rooms')} minKey="roomsMin" maxKey="roomsMax" />
      <RangeChip label={t('filters.year')} minKey="yearMin" maxKey="yearMax" />
      <RangeChip label={t('filters.price')} minKey="priceMin" maxKey="priceMax" unit="kr" />
      <PropertyTypeChip />
      <StatusChip />
      <MoreFiltersChip />
      {hasFilters && (
        <button
          type="button"
          onClick={clear}
          className="ml-1 text-2xs text-fg-muted hover:text-accent"
          data-testid="reset-filters"
        >
          {t('common.resetAll', { count: filterCount })}
        </button>
      )}
    </div>
  );
}
